/**
 * API response shapes for the Hearth backend, mirroring what the
 * handlers in `api/src/handlers/` return. We define explicit interfaces
 * here (rather than `z.infer` from the shared schemas) because the
 * backend strips/transforms certain fields (e.g., token_hash never
 * appears in API responses, denormalized profile fields are added on
 * memberships).
 */

export type MembershipRole =
  | "owner"
  | "admin"
  | "member"
  | "contributor"
  | "viewer"
  | "auditor";

export type MembershipStatus = "active" | "suspended" | "removed";

export type InvitationStatus = "pending" | "accepted" | "expired" | "revoked";

export interface MembershipClaim {
  household_id: string;
  role: MembershipRole;
}

/**
 * Per-household FIRE assumption block. Drives savings rate / FIRE
 * numbers / Coast + Barista / Time-to-FI math on the dashboard tile.
 * All fields optional with code-side defaults (4% withdrawal, 7% real
 * return, 0.7× lean, 2.5× fat, retirement age 65). Phase 6 Session 1.
 *
 * Phase 6 Session 3 (2026-05-08) additive: withdrawal_strategy +
 * per-strategy knobs + spending-smile + bond-tent toggles. Default
 * strategy is `bengen_constant` when unset (preserves 6.1/6.2 numbers).
 */
export type WithdrawalStrategyType =
  | "bengen_constant"
  | "guyton_klinger"
  | "vpw"
  | "cape_based"
  | "dynamic_band";

export type SpendingSmileCurveType = "blanchett_2014";

export type VpwTableType = "bogleheads_2020";

export interface FireAssumptions {
  withdrawal_rate_pct?: number;
  expected_return_rate_pct?: number;
  lean_spend_multiplier?: number;
  fat_spend_multiplier?: number;
  birth_year_primary?: number;
  retirement_target_age?: number;
  birth_year_spouse?: number;
  social_security_pia_spouse_cents?: number;
  social_security_claim_age_spouse?: number;
  pre_medicare_healthcare_annual_cents?: number;
  annual_spend_override_cents?: number;
  barista_annual_income_cents?: number;
  // Phase 6 Session 3 — withdrawal strategy
  withdrawal_strategy?: WithdrawalStrategyType;
  gk_floor_pct?: number;
  gk_ceiling_pct?: number;
  gk_cut_threshold_pct?: number;
  gk_raise_threshold_pct?: number;
  vpw_table?: VpwTableType;
  cape_floor_pct?: number;
  cape_anchor?: number;
  dynamic_band_pct?: number;
  // Phase 6 Session 3 — spending smile
  spending_smile_enabled?: boolean;
  spending_smile_curve?: SpendingSmileCurveType;
  // Phase 6 Session 3 — bond tent
  bond_tent_enabled?: boolean;
  bond_tent_ramp_start_years?: number;
  bond_tent_ramp_end_years?: number;
  bond_tent_floor_pct?: number;
  bond_tent_ceiling_pct?: number;
  // Phase 6 Session 4 — Social Security + geographic arbitrage
  social_security_pia_cents?: number;
  social_security_claim_age?: number;
  current_city_code?: string;
}

// === Phase 6 Session 4 — scenarios engine ==================================
export type ScenarioType =
  | "job_loss"
  | "sabbatical"
  | "second_child"
  | "relocation"
  | "emergency_50k"
  | "rate_change"
  | "custom";

export interface ScenarioDelta {
  withdrawal_rate_pct?: number;
  expected_return_rate_pct?: number;
  withdrawal_strategy?: WithdrawalStrategyType;
  spending_smile_enabled?: boolean;
  spending_smile_curve?: SpendingSmileCurveType;
  bond_tent_enabled?: boolean;
  annual_spend_override_cents?: number;
  annual_spend_delta_cents?: number;
  monthly_net_savings_override_cents?: number;
  portfolio_one_time_delta_cents?: number;
  duration_months?: number;
  current_city_code?: string;
  relocate_to_city_code?: string;
}

export interface Scenario {
  scenario_id: string;
  tenant_id: string;
  household_id: string;
  name: string;
  scenario_type: ScenarioType;
  delta: ScenarioDelta;
  is_archived: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface ScenariosListResponse {
  scenarios: Scenario[];
}

export interface CreateScenarioInput {
  name: string;
  scenario_type: ScenarioType;
  delta?: ScenarioDelta;
  notes?: string;
}

export interface UpdateScenarioInput {
  name?: string;
  delta?: ScenarioDelta;
  notes?: string | null;
  is_archived?: boolean;
}

export interface HearthUser {
  user_id: string;
  tenant_id: string;
  email: string;
  display_name: string;
  primary_household_id: string;
  role: MembershipRole;
  memberships: MembershipClaim[];
  /** Bundled by /v1/me starting Phase 6 Session 1. Null when the
   *  household has not set any FIRE assumptions yet (use defaults). */
  fire_assumptions: FireAssumptions | null;
}

export interface HouseholdMember {
  user_id: string;
  household_id: string;
  role: MembershipRole;
  scopes: string[];
  status: MembershipStatus;
  invited_by: string;
  invited_at: string;
  accepted_at?: string;
  removed_at?: string;
  email: string | null;
  display_name: string | null;
}

export interface HouseholdInvitation {
  invitation_id: string;
  tenant_id: string;
  household_id: string;
  email: string;
  role: MembershipRole;
  scopes: string[];
  invited_by: string;
  invited_at: string;
  expires_at: string;
  status: InvitationStatus;
  accepted_at?: string;
  revoked_at?: string;
  revoked_by?: string;
  created_at: string;
  updated_at: string;
}

export interface InvitationPreview {
  invitation_id: string;
  household_id: string;
  household_name: string;
  role: MembershipRole;
  email: string;
  inviter_display_name: string;
  expires_at: string;
}

export interface CreateInvitationInput {
  email: string;
  household_id: string;
  role: MembershipRole;
  scopes?: string[];
}

export interface CreateInvitationOutput {
  invitation_id: string;
  expires_at: string;
}

// === Phase 2 Session 2.2b — market data + watchlists ====================

export interface DailyBar {
  date: string; // YYYY-MM-DD
  open?: number;
  high?: number;
  low?: number;
  close: number;
  volume?: number;
}

export type IndicatorScalarSeries = (number | null)[];

export interface IndicatorMacdPoint {
  macd: number | null;
  signal: number | null;
  histogram: number | null;
}

export interface IndicatorBollingerPoint {
  upper: number | null;
  middle: number | null;
  lower: number | null;
}

export interface PricesIndicators {
  sma_20?: IndicatorScalarSeries;
  sma_50?: IndicatorScalarSeries;
  sma_200?: IndicatorScalarSeries;
  ema_20?: IndicatorScalarSeries;
  ema_50?: IndicatorScalarSeries;
  ema_200?: IndicatorScalarSeries;
  rsi_14?: IndicatorScalarSeries;
  macd_12_26_9?: IndicatorMacdPoint[];
  bb_20_2?: IndicatorBollingerPoint[];
  atr_14?: IndicatorScalarSeries;
  vwap?: IndicatorScalarSeries;
}

export interface PricesRangeResponse {
  ticker: string;
  startDate: string;
  endDate: string;
  bars: DailyBar[];
  count: number;
  indicators?: PricesIndicators;
}

export interface IntradayBar {
  timestamp: string; // ISO 8601 datetime
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface IntradayResponse {
  ticker: string;
  timeframe: string; // "5min" | "1hour"
  date: string; // YYYY-MM-DD
  bars: IntradayBar[];
  count: number;
}

export interface NewsArticle {
  tiingo_id: number;
  title: string;
  description: string;
  url: string;
  source: string;
  published_date: string;
  crawl_date: string;
  tags: string[];
  tickers: string[];
}

export interface NewsResponse {
  articles: NewsArticle[];
  count: number;
  limit: number;
}

export interface Watchlist {
  watchlist_id: string;
  household_id: string;
  name: string;
  description?: string;
  tickers: string[];
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface WatchlistsListResponse {
  watchlists: Watchlist[];
  count: number;
}

export interface CreateWatchlistInput {
  name: string;
  description?: string;
  tickers: string[];
}

export interface UpdateWatchlistInput {
  name?: string;
  description?: string;
  tickers?: string[];
  is_archived?: boolean;
}

// === Phase 2 Session 2.4 — signal rules + trade journal =================

export type IndicatorKey =
  | "sma_20"
  | "sma_50"
  | "sma_200"
  | "ema_20"
  | "ema_50"
  | "ema_200"
  | "rsi_14"
  | "macd_12_26_9"
  | "bb_20_2"
  | "atr_14"
  | "vwap";

/** Subset of `IndicatorKey` valid for threshold/cross rules — multi-series
 *  indicators (`macd_12_26_9`, `bb_20_2`) target distinct rule kinds. */
export type ScalarIndicatorKey = Exclude<
  IndicatorKey,
  "macd_12_26_9" | "bb_20_2"
>;

export type SignalComparator = "<" | ">" | "<=" | ">=";

export interface ThresholdRule {
  kind: "threshold";
  indicator: ScalarIndicatorKey;
  comparator: SignalComparator;
  value: number;
}

export interface CrossRule {
  kind: "cross";
  fast: ScalarIndicatorKey;
  slow: ScalarIndicatorKey;
  direction: "up" | "down";
}

export interface BbBandRule {
  kind: "bb_band";
  band: "upper" | "lower";
  direction: "above" | "below";
}

export type SignalRuleConfig = ThresholdRule | CrossRule | BbBandRule;

export type SignalDirection = "buy" | "sell" | "info";
export type SignalMode = "single" | "all_holdings";

/**
 * Sentinel ticker value for `mode === "all_holdings"` rules. Stored on
 * the row's `ticker` and `armed_ticker` columns. Mirror of the
 * server-side constant in `shared/src/schemas/signal-rules.ts`.
 */
export const ALL_HOLDINGS_TICKER = "_ALL_" as const;

export interface SignalRule {
  signal_rule_id: string;
  household_id: string;
  name: string;
  ticker: string;
  rule: SignalRuleConfig;
  is_armed: boolean;
  last_fired_at?: string;
  last_fired_value?: number;
  is_archived: boolean;
  // Phase 15.5 additives — server fills these for legacy rows too.
  direction: SignalDirection;
  mode: SignalMode;
  cooldown_hours: number;
  last_fired_at_by_ticker?: Record<string, string>;
  last_fired_value_by_ticker?: Record<string, number | null>;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface SignalRulesListResponse {
  signal_rules: SignalRule[];
  count: number;
}

export interface CreateSignalRuleInput {
  name: string;
  /**
   * Optional when `mode === "all_holdings"`; the server overrides
   * ticker to the `ALL_HOLDINGS_TICKER` sentinel in that mode.
   */
  ticker?: string;
  rule: SignalRuleConfig;
  is_armed?: boolean;
  // Phase 15.5 additives.
  direction?: SignalDirection;
  mode?: SignalMode;
  cooldown_hours?: number;
}

export interface UpdateSignalRuleInput {
  name?: string;
  rule?: SignalRuleConfig;
  is_armed?: boolean;
  is_archived?: boolean;
  // Phase 15.5 additives.
  direction?: SignalDirection;
  mode?: SignalMode;
  cooldown_hours?: number;
  /** Required when transitioning `mode: "all_holdings" → "single"`. */
  ticker?: string;
}

export interface BacktestSignalRuleInput {
  rule: SignalRuleConfig;
  ticker: string;
  days?: number;
}

export interface BacktestSignalRuleResponse {
  ticker: string;
  days: number;
  total_bars: number;
  fire_count: number;
  fire_dates: string[];
  warning?: string;
}

// Phase 15.5 — alerts list endpoint for the History tab + chart fire markers.
export type AlertType =
  | "signal_fired"
  | (string & { readonly __opaque__?: never });

export interface Alert {
  alert_id: string;
  household_id: string;
  timestamp: string;
  alert_type: AlertType;
  severity: "info" | "warning" | "critical";
  title: string;
  body: string;
  acknowledged: boolean;
  acknowledged_at?: string;
  acknowledged_by?: string;
  emailed: boolean;
  expires_at?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  // Phase 15.5 additions for signal_fired alerts.
  fired_ticker?: string;
  direction?: SignalDirection;
}

export interface AlertsListResponse {
  alerts: Alert[];
  count: number;
  next_cursor?: string;
}

export type TradeSide = "buy" | "sell";

export interface TradeEntry {
  entry_id: string;
  household_id: string;
  ticker: string;
  side: TradeSide;
  entry_date: string;
  entry_price_cents: number;
  quantity: number;
  exit_date?: string;
  exit_price_cents?: number;
  stop_price_cents?: number;
  target_price_cents?: number;
  thesis: string;
  outcome_notes?: string;
  signal_rule_id?: string;
  closed_pnl_cents: number | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface TradeEntriesListResponse {
  entries: TradeEntry[];
  count: number;
}

export interface CreateTradeEntryInput {
  ticker: string;
  side: TradeSide;
  entry_date: string;
  entry_price_cents: number;
  quantity: number;
  exit_date?: string;
  exit_price_cents?: number;
  stop_price_cents?: number;
  target_price_cents?: number;
  thesis: string;
  outcome_notes?: string;
  signal_rule_id?: string;
}

export interface UpdateTradeEntryInput {
  exit_date?: string;
  exit_price_cents?: number;
  stop_price_cents?: number;
  target_price_cents?: number;
  thesis?: string;
  outcome_notes?: string;
  is_archived?: boolean;
}

// === Phase 3 Session 1 — accounts + holdings ============================

export type AccountType =
  | "taxable"
  | "ira_traditional"
  | "ira_roth"
  | "ira_sep"
  | "401k_traditional"
  | "401k_roth"
  | "hsa"
  | "529"
  | "utma"
  | "checking"
  | "savings"
  | "cd"
  | "money_market"
  | "credit_card"
  | "mortgage"
  | "second_mortgage"
  | "heloc"
  | "auto_loan"
  | "student_loan"
  | "personal_loan"
  | "crypto_wallet"
  | "crypto_exchange"
  | "daf"
  | "treasury_direct"
  | "i_bond"
  | "ee_bond"
  | "529_prepaid"
  | "private_investment";

export type AccountSource = "merrill_csv" | "manual" | "plaid" | "snaptrade";
export type CostBasisMethod = "average" | "fifo" | "lifo" | "specific";
export type LotTaxTerm = "long" | "short";
export type AccountOwnership = "primary" | "spouse" | "joint" | "minor" | "trust";
export type HsaMode = "receipts" | "invested";

/**
 * Display labels for `account_type`. Source of truth shared across the
 * accounts page (cards, drilldown), the create/edit form (dropdown), and
 * any future accounts filter UI. Promoted from inline-on-page in 3.2.
 */
export const ACCOUNT_TYPE_LABELS: Readonly<Record<AccountType, string>> = {
  taxable: "Taxable",
  ira_traditional: "Traditional IRA",
  ira_roth: "Roth IRA",
  ira_sep: "SEP IRA",
  "401k_traditional": "Traditional 401(k)",
  "401k_roth": "Roth 401(k)",
  hsa: "HSA",
  "529": "529",
  utma: "UTMA",
  checking: "Checking",
  savings: "Savings",
  cd: "CD",
  money_market: "Money Market",
  credit_card: "Credit Card",
  mortgage: "Mortgage",
  second_mortgage: "Second Mortgage",
  heloc: "HELOC",
  auto_loan: "Auto Loan",
  student_loan: "Student Loan",
  personal_loan: "Personal Loan",
  crypto_wallet: "Crypto (Wallet)",
  crypto_exchange: "Crypto (Exchange)",
  daf: "Donor-Advised Fund",
  treasury_direct: "TreasuryDirect",
  i_bond: "I Bond",
  ee_bond: "EE Bond",
  "529_prepaid": "529 Prepaid",
  private_investment: "Private Investment",
};

/**
 * Account-class membership sets used by `<AccountForm>` to decide which
 * conditional sections to render and by `<AccountCard>` to flip G/L
 * sign convention for liabilities (debt principal going DOWN renders
 * green). Each `AccountType` belongs to exactly one class except `hsa`,
 * which can be rendered as either receipts-only OR invested + receipts
 * depending on `hsa_mode`.
 */
export const INVESTMENT_ACCOUNT_TYPES: ReadonlySet<AccountType> = new Set([
  "taxable",
  "ira_traditional",
  "ira_roth",
  "ira_sep",
  "401k_traditional",
  "401k_roth",
  "529",
  "utma",
  "private_investment",
  "treasury_direct",
  "i_bond",
  "ee_bond",
  "529_prepaid",
  "daf",
]);

export const CASH_ACCOUNT_TYPES: ReadonlySet<AccountType> = new Set([
  "checking",
  "savings",
  "cd",
  "money_market",
]);

export const LIABILITY_ACCOUNT_TYPES: ReadonlySet<AccountType> = new Set([
  "credit_card",
  "mortgage",
  "second_mortgage",
  "heloc",
  "auto_loan",
  "student_loan",
  "personal_loan",
]);

export const CRYPTO_ACCOUNT_TYPES: ReadonlySet<AccountType> = new Set([
  "crypto_wallet",
  "crypto_exchange",
]);

export interface AccountSummary {
  account_id: string;
  household_id: string;
  name: string;
  institution: string;
  account_type: AccountType;
  external_id?: string;
  source?: AccountSource;
  cost_basis_method?: CostBasisMethod;
  is_tax_advantaged: boolean;
  position_count: number;
  total_value_cents: number;
  positions_value_cents: number;
  total_cost_basis_cents: number;
  unrealized_gain_loss_cents: number;
  cash_balance_cents: number;
  margin_balance_cents: number | null;
  as_of_date?: string;
  balances_as_of_date?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  // Phase 3 Session 2 — additive fields surfaced for the edit form.
  ownership?: AccountOwnership;
  notes?: string;
  manual_balance_cents?: number;
  manual_balance_as_of?: string;
  beneficiary_member_id?: string;
  // Liability fields
  originating_principal_cents?: number;
  current_principal_cents?: number;
  interest_rate_pct?: number;
  payoff_date?: string;
  minimum_payment_cents?: number;
  payment_due_day_of_month?: number;
  apr_pct?: number;
  credit_limit_cents?: number;
  rewards_program?: string;
  annual_fee_cents?: number;
  // HSA + crypto
  hsa_mode?: HsaMode;
  chain?: string;
  wallet_address?: string;
  // Phase 5 Session 5 — 529 / UTMA family-finance fields.
  plan_state_code?: string;
  account_owner_member_id?: string;
}

/**
 * Body for `POST /v1/accounts`. The backend forces `source = "manual"`,
 * `is_archived = false`, and ownership defaults; everything else is
 * optional and conditionally populated by the form.
 */
export interface CreateAccountInput {
  name: string;
  institution: string;
  account_type: AccountType;
  ownership: AccountOwnership;
  is_tax_advantaged?: boolean;
  notes?: string;
  cost_basis_method?: CostBasisMethod;
  manual_balance_cents?: number;
  manual_balance_as_of?: string;
  beneficiary_member_id?: string;
  originating_principal_cents?: number;
  current_principal_cents?: number;
  interest_rate_pct?: number;
  payoff_date?: string;
  minimum_payment_cents?: number;
  payment_due_day_of_month?: number;
  apr_pct?: number;
  credit_limit_cents?: number;
  rewards_program?: string;
  annual_fee_cents?: number;
  hsa_mode?: HsaMode;
  chain?: string;
  wallet_address?: string;
  // Phase 5 Session 5 — 529 / UTMA family-finance fields.
  plan_state_code?: string;
  account_owner_member_id?: string;
}

/**
 * Body for `PATCH /v1/accounts/{account_id}`. All fields optional; at
 * least one must be set. Broker-sourced rows (`source === "merrill_csv"`)
 * accept only the metadata-protected subset; the backend rejects edits
 * to broker-derived fields with 400.
 */
export type UpdateAccountInput = Partial<CreateAccountInput> & {
  is_archived?: boolean;
};

export interface AccountsListResponse {
  accounts: AccountSummary[];
  count: number;
  totals: {
    total_value_cents: number;
    positions_value_cents: number;
    total_cost_basis_cents: number;
    unrealized_gain_loss_cents: number;
    cash_balance_cents: number;
    margin_balance_cents: number;
    /** The oldest date behind any cash or margin figure in these totals. */
    balances_as_of_date: string | null;
  };
}

export interface AccountLotDetail {
  lot_id: string;
  purchase_date: string;
  quantity: number;
  cost_basis_cents: number;
  unit_cost_cents: number | null;
  tax_term: LotTaxTerm | null;
  source: AccountSource | null;
  created_at: string;
}

export interface AccountHoldingDetail {
  account_id: string;
  ticker: string;
  quantity: number;
  cost_basis_cents: number | null;
  weighted_avg_unit_cost_cents: number | null;
  value_cents: number | null;
  last_price_cents: number | null;
  unrealized_gain_loss_cents: number | null;
  as_of_date: string | null;
  source: AccountSource | null;
  lots: AccountLotDetail[];
}

export interface AccountDrilldownDetail {
  account_id: string;
  household_id: string;
  name: string;
  institution: string;
  account_type: AccountType;
  external_id: string | null;
  source: AccountSource | null;
  cost_basis_method: CostBasisMethod | null;
  is_tax_advantaged: boolean;
  cash_balance_cents: number | null;
  margin_balance_cents: number | null;
  as_of_date: string | null;
  // Phase 3 Session 2 — additive fields surfaced for the edit form.
  ownership: AccountOwnership | null;
  notes: string | null;
  manual_balance_cents: number | null;
  manual_balance_as_of: string | null;
  beneficiary_member_id: string | null;
  originating_principal_cents: number | null;
  current_principal_cents: number | null;
  interest_rate_pct: number | null;
  payoff_date: string | null;
  minimum_payment_cents: number | null;
  payment_due_day_of_month: number | null;
  apr_pct: number | null;
  credit_limit_cents: number | null;
  rewards_program: string | null;
  annual_fee_cents: number | null;
  hsa_mode: HsaMode | null;
  chain: string | null;
  wallet_address: string | null;
  // Phase 5 Session 5 — 529 / UTMA family-finance fields.
  plan_state_code: string | null;
  account_owner_member_id: string | null;
  is_archived: boolean;
}

export interface AccountHoldingsResponse {
  account: AccountDrilldownDetail;
  holdings: AccountHoldingDetail[];
  count: number;
}

/** Body for `POST /v1/accounts/{account_id}/lots`. */
export interface CreateLotInput {
  ticker: string;
  purchase_date: string;
  quantity: number;
  cost_basis_cents: number;
  unit_cost_cents?: number;
  tax_term?: LotTaxTerm;
}

/**
 * Body for `PATCH /v1/accounts/{account_id}/lots/{lot_id}`. All fields
 * optional; at least one must be set. Backend rejects updates on
 * broker-sourced lots (source !== "manual") with 400. Changing
 * `ticker` or `purchase_date` triggers a SK rewrite on the backend
 * (Delete-old-SK + Put-new-SK in one TransactWriteItems).
 */
export interface UpdateLotInput {
  ticker?: string;
  purchase_date?: string;
  quantity?: number;
  cost_basis_cents?: number;
  unit_cost_cents?: number;
  tax_term?: LotTaxTerm;
}

// === Phase 3 Session 3 — sale + DRIP transaction types ====================

/**
 * Body for `POST /v1/accounts/{account_id}/transactions` — sale branch.
 * `lot_ids` is required when the account's cost_basis_method is
 * "specific"; ignored otherwise. The backend's pure-function math
 * (`applySaleAgainstLots`) picks lots per the method and computes the
 * realized PnL + LT/ST split server-side.
 */
export interface CreateSaleInput {
  transaction_type: "sell";
  ticker: string;
  quantity: number;
  sale_price_cents: number;
  sale_date: string;
  lot_ids?: string[];
  cost_basis_method_override?: CostBasisMethod;
  notes?: string;
}

/**
 * Body for `POST /v1/accounts/{account_id}/transactions` — DRIP branch.
 * Synthesizes a new lot with `source: "drip"`. Phase 3 Session 3 ships
 * the endpoint; the SPA UI for DRIP entry lands in Phase 11.
 */
export interface CreateDripInput {
  transaction_type: "drip_purchase";
  ticker: string;
  quantity: number;
  cost_basis_cents: number;
  purchase_date: string;
  dividend_id?: string;
  notes?: string;
}

export interface SaleReductionDetail {
  lot_id: string;
  purchase_date: string;
  quantity_reduced: number;
  cost_basis_reduced_cents: number;
  proceeds_cents: number;
  realized_pnl_cents: number;
  tax_term: LotTaxTerm;
}

export interface SaleResult {
  transaction: Record<string, unknown>;
  reductions: SaleReductionDetail[];
  realized_pnl_cents: number;
  lt_pnl_cents: number;
  st_pnl_cents: number;
}

// === Phase 4 Session 1 — net worth dashboard ==============================

export type AssetClass =
  | "investment"
  | "cash"
  | "liability"
  | "alt"
  | "crypto";

export type NetWorthWindow = "24m" | "5y" | "all";

export interface NetWorthAccountValuation {
  account_id: string;
  name: string;
  account_type: AccountType;
  ownership: AccountOwnership;
  asset_class: AssetClass;
  /** Signed cents — liabilities contribute negative values. */
  value_cents: number;
  /**
   * Phase 16e (2026-05-10): positions-only valuation — holdings × prices,
   * excluding cash sleeves and margin. 0 for cash + liability accounts.
   * Mirror of `shared/src/util/networth.ts`.
   */
  positions_value_cents?: number;
}

export interface NetWorthSnapshot {
  household_id: string;
  tenant_id: string;
  date: string; // ISO YYYY-MM-DD
  total_assets_cents: number;
  total_liabilities_cents: number;
  net_worth_cents: number;
  cash_cents: number;
  margin_cents: number;
  by_account_type: Record<string, number>;
  by_asset_class: Record<AssetClass, number>;
  by_owner: Record<string, number>;
  by_account: NetWorthAccountValuation[];
  computed_at: string;
  source?: "backfill" | "scheduled";
  account_breakdown?: Record<string, number>;
  // Phase 16e additives — positions-only basis. See
  // shared/src/schemas/net-worth.ts for the contract.
  portfolio_value_cents?: number;
  portfolio_by_account?: Record<string, number>;
}

/**
 * Phase 16d (2026-05-10): lightweight series row returned by
 * `/v1/networth?window=...`. The handler strips the heavy breakdown
 * objects on series rows to stay under the 5s Lambda timeout for
 * `?window=all`; only the chart-relevant fields ride along. The full
 * `latest` snapshot still carries everything.
 *
 * **Phase 16e (2026-05-10)**: `portfolio_value_cents` +
 * `portfolio_by_account` ride along too — they're roughly the same size
 * as `account_breakdown` and power the /dashboard portfolio-mode toggle
 * + /portfolio Performance tab. Pre-16e rows lack the fields; the SPA
 * treats missing as 0/empty.
 */
export interface NetWorthSeriesRow {
  date: string;
  net_worth_cents: number;
  total_assets_cents: number;
  total_liabilities_cents: number;
  cash_cents: number;
  margin_cents: number;
  source?: "backfill" | "scheduled";
  account_breakdown?: Record<string, number>;
  portfolio_value_cents?: number;
  portfolio_by_account?: Record<string, number>;
}

export interface NetWorthListResponse {
  snapshots: NetWorthSeriesRow[];
  latest: NetWorthSnapshot | null;
  window: NetWorthWindow;
}

export interface NetWorthSnapshotResponse {
  snapshot: NetWorthSnapshot;
}

// === Phase 4 Session 2 — notes / decisions / messages =====================

export type NoteRelatedEntityType =
  | "account"
  | "holding"
  | "lot"
  | "transaction"
  | "goal"
  | "bill"
  | "tax_year"
  | "tax_event"
  | "insurance_policy"
  | "estate_item"
  | "document"
  | "donation"
  | "scenario"
  | "housing_unit"
  | "housing_assessment"
  | "income_event"
  | "equity_grant"
  | "alert"
  | "family_member"
  | "household"
  | "watchlist"
  // Phase 5 Session 1 — `bill`, `family_member`, `income_event`,
  // `equity_grant` were already in the Phase-0 enum. `budget_category`
  // is new for category-level negotiation/discussion threads.
  | "budget_category"
  // Phase 5 Session 5 — college plans grow notes for "decisions about
  // school targets / contribution ramp-ups / what we agreed at the
  // annual review."
  | "college_plan";

export type NoteVisibility = "personal" | "household" | "auditor";

export interface Note {
  note_id: string;
  /** Compound SK `<timestamp>#<note_id>`. URL-encoded by api-modules. */
  note_sk: string;
  household_id: string;
  timestamp: string;
  author_user_id: string;
  related_entity_type: NoteRelatedEntityType;
  related_entity_id: string;
  body: string;
  visibility: NoteVisibility;
  pinned: boolean;
  archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface NotesListResponse {
  notes: Note[];
  count: number;
}

export interface CreateNoteInput {
  related_entity_type: NoteRelatedEntityType;
  related_entity_id: string;
  body: string;
  visibility?: NoteVisibility;
  pinned?: boolean;
}

export interface UpdateNoteInput {
  body?: string;
  visibility?: NoteVisibility;
  pinned?: boolean;
  archived?: boolean;
}

// === decisions =============================================================

export type DecisionStatus = "active" | "superseded" | "completed";

export interface DecisionOption {
  option: string;
  pros?: string;
  cons?: string;
}

export interface Decision {
  decision_id: string;
  household_id: string;
  decision_made_at: string;
  decided_by_user_ids: string[];
  title: string;
  context: string;
  options_considered: DecisionOption[];
  chosen_option: string;
  rationale: string;
  related_entity_type?: string;
  related_entity_id?: string;
  revisit_date?: string;
  status: DecisionStatus;
  superseded_by_decision_id?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface DecisionsListResponse {
  decisions: Decision[];
  count: number;
}

export interface CreateDecisionInput {
  title: string;
  context: string;
  chosen_option: string;
  rationale: string;
  options_considered?: DecisionOption[];
  related_entity_type?: string;
  related_entity_id?: string;
  revisit_date?: string;
  decided_by_user_ids?: string[];
  status?: DecisionStatus;
}

export interface UpdateDecisionInput {
  revisit_date?: string;
  status?: DecisionStatus;
  superseded_by_decision_id?: string;
  is_archived?: boolean;
}

// === messages =============================================================

export interface MessageThread {
  thread_id: string;
  household_id: string;
  title: string;
  participant_user_ids: string[];
  related_entity_type?: string;
  related_entity_id?: string;
  last_message_at?: string;
  last_message_preview?: string;
  /** Caller's unread count, derived server-side from `unread_count_by_user`. */
  unread_count: number;
  pinned: boolean;
  archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface MessageThreadsListResponse {
  threads: MessageThread[];
  count: number;
}

export interface CreateMessageThreadInput {
  title: string;
  participant_user_ids?: string[];
  related_entity_type?: string;
  related_entity_id?: string;
}

export interface UpdateMessageThreadInput {
  title?: string;
  pinned?: boolean;
  archived?: boolean;
}

export interface Message {
  message_id: string;
  thread_id: string;
  timestamp: string;
  household_id: string;
  author_user_id: string;
  body: string;
  related_entity_type?: string;
  related_entity_id?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface MessagesListResponse {
  messages: Message[];
  thread: MessageThread;
  count: number;
}

export interface CreateMessageInput {
  body: string;
  related_entity_type?: string;
  related_entity_id?: string;
}

// === Phase 5 Session 1 — family members ====================================

export type FamilyRelationship =
  | "self"
  | "spouse"
  | "parent"
  | "child"
  | "sibling"
  | "grandparent"
  | "grandchild"
  | "other";

export interface FamilyMember {
  member_id: string;
  household_id: string;
  name: string;
  relationship: FamilyRelationship;
  date_of_birth?: string;
  member_user_id?: string;
  email?: string;
  /** Phase 13 — work anniversary date for the daily celebration cron. */
  work_anniversary_date?: string;
  notes?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface FamilyMembersListResponse {
  family_members: FamilyMember[];
  count: number;
}

export interface CreateFamilyMemberInput {
  name: string;
  relationship: FamilyRelationship;
  date_of_birth?: string;
  member_user_id?: string;
  email?: string;
  work_anniversary_date?: string;
  notes?: string;
}

export interface UpdateFamilyMemberInput {
  name?: string;
  relationship?: FamilyRelationship;
  date_of_birth?: string;
  member_user_id?: string;
  email?: string;
  work_anniversary_date?: string;
  notes?: string;
  is_archived?: boolean;
}

// === Phase 5 Session 2 — bills =============================================

export type BillRecurrence = "monthly" | "quarterly" | "annual" | "one_time";

export type PaymentMethod =
  | "credit_card"
  | "debit"
  | "ach"
  | "check"
  | "cash"
  | "venmo"
  | "zelle"
  | "paypal"
  | "other";

/**
 * Display labels for `recurrence` and `payment_method`. Source of truth
 * shared across the bills page (cards, edit form, chips) and any future
 * bills filter UI.
 */
export const BILL_RECURRENCE_LABELS: Readonly<Record<BillRecurrence, string>> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
  one_time: "One time",
};

export const PAYMENT_METHOD_LABELS: Readonly<Record<PaymentMethod, string>> = {
  credit_card: "Credit card",
  debit: "Debit",
  ach: "ACH",
  check: "Check",
  cash: "Cash",
  venmo: "Venmo",
  zelle: "Zelle",
  paypal: "PayPal",
  other: "Other",
};

export interface BillPriceObservation {
  amount_cents: number;
  observed_at: string; // ISO timestamp
}

export interface Bill {
  bill_id: string;
  household_id: string;
  name: string;
  amount_cents: number;
  due_date: string; // YYYY-MM-DD
  recurrence: BillRecurrence;
  start_date: string; // YYYY-MM-DD; anchor for nextDueDate
  autopay: boolean;
  category?: string;
  account_id?: string;
  payee?: string;
  payment_method?: PaymentMethod;
  last_paid_date?: string;
  is_archived: boolean;
  notes?: string;
  description?: string;
  // Phase 5 Session 6 additive
  amount_cents_history?: BillPriceObservation[];
  expected_uses_per_month?: number;
  price_change_alert_threshold_cents?: number;
  price_change_alert_threshold_pct?: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface BillsListResponse {
  bills: Bill[];
  count: number;
}

export interface CreateBillInput {
  name: string;
  amount_cents: number;
  due_date: string;
  recurrence: BillRecurrence;
  start_date: string;
  autopay?: boolean;
  category?: string;
  account_id?: string;
  payee?: string;
  payment_method?: PaymentMethod;
  notes?: string;
  description?: string;
  // Phase 5 Session 6 additive
  expected_uses_per_month?: number;
  price_change_alert_threshold_cents?: number;
  price_change_alert_threshold_pct?: number;
}

/**
 * `UpdateBillInput` accepts EITHER the mark-paid shortcut OR a partial
 * field update — never both. The bills/update.ts handler enforces this
 * at the boundary.
 */
export type UpdateBillInput =
  | { mark_paid: true }
  | {
      mark_paid?: false;
      name?: string;
      amount_cents?: number;
      due_date?: string;
      recurrence?: BillRecurrence;
      start_date?: string;
      autopay?: boolean;
      category?: string;
      account_id?: string;
      payee?: string;
      payment_method?: PaymentMethod;
      last_paid_date?: string;
      is_archived?: boolean;
      notes?: string;
      description?: string;
      // Phase 5 Session 6 additive
      expected_uses_per_month?: number;
      price_change_alert_threshold_cents?: number;
      price_change_alert_threshold_pct?: number;
    };

// === Phase 5 Session 2 — budget categories ================================

export interface BudgetCategory {
  category_id: string;
  household_id: string;
  name: string;
  parent_category_id?: string;
  monthly_budget_cents?: number;
  is_essential: boolean;
  is_active: boolean;
  color?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface BudgetCategoriesListResponse {
  budget_categories: BudgetCategory[];
  count: number;
}

export interface CreateBudgetCategoryInput {
  name: string;
  parent_category_id?: string;
  monthly_budget_cents?: number;
  is_essential?: boolean;
  color?: string;
}

export interface UpdateBudgetCategoryInput {
  name?: string;
  parent_category_id?: string;
  monthly_budget_cents?: number;
  is_essential?: boolean;
  is_active?: boolean;
  color?: string;
}

// === Phase 5 Session 2 — spending transactions ============================

export type SpendingTransactionSource = "manual" | "csv" | "plaid" | "import";

export interface SpendingTransaction {
  tx_id: string;
  household_id: string;
  date: string; // YYYY-MM-DD
  account_id: string;
  amount_cents: number;
  merchant: string;
  category_id?: string;
  description?: string;
  payment_method?: PaymentMethod;
  is_recurring: boolean;
  source: SpendingTransactionSource;
  source_ref?: string;
  needs_review: boolean;
  tags: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface SpendingTransactionsListResponse {
  spending_transactions: SpendingTransaction[];
  count: number;
  from: string;
  to: string;
  category_id?: string;
  account_id?: string;
}

export interface CreateSpendingTransactionInput {
  date: string;
  account_id: string;
  amount_cents: number;
  merchant: string;
  category_id?: string;
  description?: string;
  payment_method?: PaymentMethod;
  is_recurring?: boolean;
  source_ref?: string;
  needs_review?: boolean;
  tags?: string[];
  notes?: string;
  // `source` is forced to "manual" by the server; clients shouldn't
  // include it.
}

export interface UpdateSpendingTransactionInput {
  account_id?: string;
  amount_cents?: number;
  merchant?: string;
  category_id?: string;
  description?: string;
  payment_method?: PaymentMethod;
  is_recurring?: boolean;
  needs_review?: boolean;
  tags?: string[];
  notes?: string;
  // `date` cannot be changed via PATCH (rewrites SK); use DELETE + create.
  // `source` is locked to "manual"; cannot be changed.
}

// === Phase 5 Session 3 — income events =====================================

export type IncomeEventType =
  | "salary_paycheck"
  | "rsu_vest"
  | "iso_grant"
  | "iso_exercise"
  | "nso_grant"
  | "nso_exercise"
  | "espp_purchase"
  | "bonus"
  | "side_income"
  | "other";

export const INCOME_EVENT_TYPE_LABELS: Readonly<
  Record<IncomeEventType, string>
> = {
  salary_paycheck: "Salary paycheck",
  rsu_vest: "RSU vest",
  iso_grant: "ISO grant",
  iso_exercise: "ISO exercise",
  nso_grant: "NSO grant",
  nso_exercise: "NSO exercise",
  espp_purchase: "ESPP purchase",
  bonus: "Bonus",
  side_income: "Side income",
  other: "Other",
};

export interface IncomeEvent {
  event_id: string;
  household_id: string;
  date: string; // YYYY-MM-DD
  amount_cents: number;
  event_type: IncomeEventType;
  payer: string;
  currency: string; // ISO 4217
  gross_cents?: number;
  net_cents?: number;
  withholding_cents?: number;
  equity_grant_id?: string;
  notes?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface IncomeEventsListResponse {
  income_events: IncomeEvent[];
  count: number;
  from?: string;
  to?: string;
  event_type?: IncomeEventType;
}

export interface CreateIncomeEventInput {
  date: string;
  amount_cents: number;
  event_type: IncomeEventType;
  payer: string;
  currency?: string;
  gross_cents?: number;
  net_cents?: number;
  withholding_cents?: number;
  equity_grant_id?: string;
  notes?: string;
}

export interface UpdateIncomeEventInput {
  amount_cents?: number;
  event_type?: IncomeEventType;
  payer?: string;
  currency?: string;
  gross_cents?: number;
  net_cents?: number;
  withholding_cents?: number;
  equity_grant_id?: string;
  notes?: string;
  is_archived?: boolean;
}

// === Phase 12 — recurring paycheck schedules ===============================

export type IncomeScheduleType =
  | "salary_w2"
  | "contractor_1099"
  | "side_income";

export const INCOME_SCHEDULE_TYPE_LABELS: Readonly<
  Record<IncomeScheduleType, string>
> = {
  salary_w2: "Salary (W-2)",
  contractor_1099: "Contractor (1099)",
  side_income: "Side income",
};

export type PaycheckFrequency =
  | "weekly"
  | "bi_weekly"
  | "semi_monthly"
  | "monthly"
  | "quarterly"
  | "annual";

export const PAYCHECK_FREQUENCY_LABELS: Readonly<
  Record<PaycheckFrequency, string>
> = {
  weekly: "Weekly (52/yr)",
  bi_weekly: "Bi-weekly (every 2 weeks; 26/yr)",
  semi_monthly: "Semi-monthly (twice a month; 24/yr)",
  monthly: "Monthly (12/yr)",
  quarterly: "Quarterly (4/yr)",
  annual: "Annual (1/yr)",
};

export interface PaycheckPreTaxDeductions {
  k401k_traditional_cents?: number;
  roth_k401k_cents?: number;
  after_tax_k401k_cents?: number;
  hsa_cents?: number;
  medical_premium_cents?: number;
  dental_premium_cents?: number;
  vision_premium_cents?: number;
  fsa_healthcare_cents?: number;
  fsa_dependent_care_cents?: number;
  commuter_cents?: number;
  other_cents?: number;
}

export interface PaycheckPostTaxDeductions {
  espp_cents?: number;
  life_insurance_cents?: number;
  other_cents?: number;
}

export interface IncomeSchedule {
  schedule_id: string;
  household_id: string;
  schedule_type: IncomeScheduleType;
  payer: string;
  user_id?: string;
  is_active: boolean;
  effective_from: string;
  end_date?: string;
  annual_gross_cents?: number;
  per_pay_gross_cents?: number;
  frequency?: PaycheckFrequency;
  anchor_date?: string;
  semi_monthly_days?: [number, number];
  expected_withholding_pct?: number;
  pre_tax_deductions_breakdown?: PaycheckPreTaxDeductions;
  post_tax_deductions_breakdown?: PaycheckPostTaxDeductions;
  net_pay_account_id?: string;
  k401k_account_id?: string;
  hsa_account_id?: string;
  k401k_employer_match_pct?: number;
  k401k_employer_match_cap_pct?: number;
  hsa_employer_contribution_per_pay_cents?: number;
  estimated_tax_pct?: number;
  last_materialized_pay_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface IncomeSchedulesListResponse {
  income_schedules: IncomeSchedule[];
  count: number;
}

export interface CreateIncomeScheduleInput {
  schedule_type: IncomeScheduleType;
  payer: string;
  user_id?: string;
  is_active?: boolean;
  effective_from: string;
  end_date?: string;
  annual_gross_cents?: number;
  per_pay_gross_cents?: number;
  frequency?: PaycheckFrequency;
  anchor_date?: string;
  semi_monthly_days?: [number, number];
  expected_withholding_pct?: number;
  pre_tax_deductions_breakdown?: PaycheckPreTaxDeductions;
  post_tax_deductions_breakdown?: PaycheckPostTaxDeductions;
  net_pay_account_id?: string;
  k401k_account_id?: string;
  hsa_account_id?: string;
  k401k_employer_match_pct?: number;
  k401k_employer_match_cap_pct?: number;
  hsa_employer_contribution_per_pay_cents?: number;
  estimated_tax_pct?: number;
  notes?: string;
}

export type UpdateIncomeScheduleInput = Partial<CreateIncomeScheduleInput>;

// === Phase 12 — charity (donations + DAF accounts) ========================

export type DonationPaymentMethod =
  | "cash"
  | "check"
  | "ach"
  | "credit_card"
  | "stock"
  | "daf_grant"
  | "qcd"
  | "other";

export const DONATION_PAYMENT_METHOD_LABELS: Readonly<
  Record<DonationPaymentMethod, string>
> = {
  cash: "Cash",
  check: "Check",
  ach: "ACH / bank transfer",
  credit_card: "Credit card",
  stock: "Stock (appreciated)",
  daf_grant: "DAF grant",
  qcd: "QCD (IRA, age 70½+)",
  other: "Other",
};

export interface Donation {
  donation_id: string;
  household_id: string;
  date: string;
  charity_name: string;
  charity_ein?: string;
  amount_cents: number;
  payment_method: DonationPaymentMethod;
  tax_year: number;
  linked_account_id?: string;
  linked_receipt_document_id?: string;
  cost_basis_cents?: number;
  fmv_at_donation_cents?: number;
  ticker?: string;
  quantity?: number;
  linked_lot_id?: string;
  acquired_date?: string;
  notes?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface DonationsListResponse {
  donations: Donation[];
  count: number;
  ytd_total_cents: number;
  by_tax_year: { tax_year: number; total_cents: number }[];
}

export interface CreateDonationInput {
  date: string;
  charity_name: string;
  charity_ein?: string;
  amount_cents: number;
  payment_method: DonationPaymentMethod;
  tax_year: number;
  linked_account_id?: string;
  linked_receipt_document_id?: string;
  cost_basis_cents?: number;
  fmv_at_donation_cents?: number;
  ticker?: string;
  quantity?: number;
  linked_lot_id?: string;
  acquired_date?: string;
  notes?: string;
}

export type DafProvider =
  | "fidelity_charitable"
  | "schwab_charitable"
  | "vanguard_charitable"
  | "national_philanthropic_trust"
  | "other";

export const DAF_PROVIDER_LABELS: Readonly<Record<DafProvider, string>> = {
  fidelity_charitable: "Fidelity Charitable",
  schwab_charitable: "Schwab Charitable",
  vanguard_charitable: "Vanguard Charitable",
  national_philanthropic_trust: "National Philanthropic Trust",
  other: "Other",
};

export interface DafAccount {
  daf_account_id: string;
  household_id: string;
  provider: DafProvider;
  fund_name: string;
  account_number_last4?: string;
  balance_cents: number;
  last_synced_at?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface DafAccountsListResponse {
  daf_accounts: DafAccount[];
  count: number;
  total_balance_cents: number;
}

export interface CreateDafAccountInput {
  provider: DafProvider;
  fund_name: string;
  account_number_last4?: string;
  balance_cents: number;
  notes?: string;
}

// === Phase 5 Session 3 — equity grants =====================================

export type EquityGrantType = "RSU" | "ISO" | "NSO" | "ESPP" | "RSA";

export const EQUITY_GRANT_TYPE_LABELS: Readonly<
  Record<EquityGrantType, string>
> = {
  RSU: "RSU",
  ISO: "ISO",
  NSO: "NSO",
  ESPP: "ESPP",
  RSA: "RSA",
};

export interface VestEvent {
  date: string; // YYYY-MM-DD
  shares: number;
  is_cliff?: boolean;
}

export interface BlackoutWindow {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
  reason: string;
}

export interface EquityGrant {
  grant_id: string;
  household_id: string;
  grant_type: EquityGrantType;
  ticker: string;
  grant_date: string;
  total_shares: number;
  strike_price_cents?: number;
  vest_events: VestEvent[];
  expiration_date?: string;
  blackout_windows: BlackoutWindow[];
  most_recent_fmv_cents?: number;
  fmv_as_of_date?: string;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface EquityGrantsListResponse {
  equity_grants: EquityGrant[];
  count: number;
}

export interface CreateEquityGrantInput {
  grant_type: EquityGrantType;
  ticker: string;
  grant_date: string;
  total_shares: number;
  strike_price_cents?: number;
  vest_events?: VestEvent[];
  expiration_date?: string;
  blackout_windows?: BlackoutWindow[];
  most_recent_fmv_cents?: number;
  fmv_as_of_date?: string;
  notes?: string;
}

export interface UpdateEquityGrantInput {
  ticker?: string;
  total_shares?: number;
  strike_price_cents?: number;
  vest_events?: VestEvent[];
  expiration_date?: string;
  blackout_windows?: BlackoutWindow[];
  most_recent_fmv_cents?: number;
  fmv_as_of_date?: string;
  is_active?: boolean;
  notes?: string;
}

// === Phase 5.4: Housing ===================================================

export type OwnershipType =
  | "coop"
  | "condo"
  | "single_family"
  | "multi_family"
  | "townhouse"
  | "land"
  | "rental_owned"
  | "timeshare";

export const OWNERSHIP_TYPE_LABELS: Readonly<Record<OwnershipType, string>> = {
  coop: "Co-op",
  condo: "Condo",
  single_family: "Single-family",
  multi_family: "Multi-family",
  townhouse: "Townhouse",
  land: "Land",
  rental_owned: "Rental (owned)",
  timeshare: "Timeshare",
};

export type AcquisitionMethod =
  | "purchase"
  | "inheritance"
  | "gift"
  | "1031_exchange";

export type ValuationSource =
  | "manual"
  | "zillow"
  | "redfin"
  | "appraisal"
  | "comp_analysis";

export type FlipTaxBasis = "sale_price" | "profit" | "shares" | "tiered";

export interface HousingUnit {
  unit_id: string;
  household_id: string;
  ownership_type: OwnershipType;
  is_primary_residence: boolean;
  address: string;
  unit_designation?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  purchase_date?: string;
  purchase_price_cents?: number;
  closing_costs_cents?: number;
  acquisition_method?: AcquisitionMethod;
  current_value_cents?: number;
  current_value_as_of?: string;
  current_value_source?: ValuationSource;
  shares_held?: number;
  total_shares_outstanding?: number;
  flip_tax_pct?: number;
  flip_tax_basis?: FlipTaxBasis;
  underlying_mortgage_share_cents?: number;
  square_feet?: number;
  bedrooms?: number;
  bathrooms?: number;
  year_built?: number;
  linked_account_ids: string[];
  insurance_policy_ids: string[];
  hoa_or_coop_account_id?: string;
  notes?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface HousingUnitsListResponse {
  housing_units: HousingUnit[];
  count: number;
}

export interface CreateHousingUnitInput {
  ownership_type: OwnershipType;
  is_primary_residence?: boolean;
  address: string;
  unit_designation?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  purchase_date?: string;
  purchase_price_cents?: number;
  closing_costs_cents?: number;
  acquisition_method?: AcquisitionMethod;
  current_value_cents?: number;
  current_value_as_of?: string;
  current_value_source?: ValuationSource;
  shares_held?: number;
  total_shares_outstanding?: number;
  flip_tax_pct?: number;
  flip_tax_basis?: FlipTaxBasis;
  underlying_mortgage_share_cents?: number;
  square_feet?: number;
  bedrooms?: number;
  bathrooms?: number;
  year_built?: number;
  linked_account_ids?: string[];
  insurance_policy_ids?: string[];
  hoa_or_coop_account_id?: string;
  notes?: string;
}

export interface UpdateHousingUnitInput {
  ownership_type?: OwnershipType;
  is_primary_residence?: boolean;
  address?: string;
  unit_designation?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  purchase_date?: string;
  purchase_price_cents?: number;
  closing_costs_cents?: number;
  acquisition_method?: AcquisitionMethod;
  current_value_cents?: number;
  current_value_as_of?: string;
  current_value_source?: ValuationSource;
  shares_held?: number;
  total_shares_outstanding?: number;
  flip_tax_pct?: number;
  flip_tax_basis?: FlipTaxBasis;
  underlying_mortgage_share_cents?: number;
  square_feet?: number;
  bedrooms?: number;
  bathrooms?: number;
  year_built?: number;
  linked_account_ids?: string[];
  insurance_policy_ids?: string[];
  hoa_or_coop_account_id?: string;
  notes?: string;
  is_archived?: boolean;
}

export type HousingExpenseType =
  | "maintenance"
  | "hoa_fee"
  | "assessment_special"
  | "assessment_capital"
  | "property_tax"
  | "mortgage_payment"
  | "heloc_payment"
  | "insurance_premium"
  | "utility"
  | "repair"
  | "improvement_capitalized"
  | "other";

export const HOUSING_EXPENSE_TYPE_LABELS: Readonly<
  Record<HousingExpenseType, string>
> = {
  maintenance: "Maintenance (co-op)",
  hoa_fee: "HOA fee",
  assessment_special: "Special assessment",
  assessment_capital: "Capital assessment",
  property_tax: "Property tax",
  mortgage_payment: "Mortgage payment",
  heloc_payment: "HELOC payment",
  insurance_premium: "Insurance premium",
  utility: "Utility",
  repair: "Repair",
  improvement_capitalized: "Capitalized improvement",
  other: "Other",
};

export type HelocUsePurpose =
  | "home_improvement"
  | "debt_consolidation"
  | "other";

export const HELOC_USE_PURPOSE_LABELS: Readonly<Record<HelocUsePurpose, string>> = {
  home_improvement: "Home improvement (deductible)",
  debt_consolidation: "Debt consolidation (not deductible)",
  other: "Other (not deductible)",
};

export interface HousingExpense {
  expense_id: string;
  unit_id: string;
  household_id: string;
  expense_type: HousingExpenseType;
  amount_cents: number;
  payment_date: string;
  period_covered?: string;
  deductible_mortgage_interest_cents?: number;
  deductible_property_tax_cents?: number;
  non_deductible_portion_cents?: number;
  payment_account_id?: string;
  receipt_document_id?: string;
  vendor_or_payee?: string;
  is_recurring: boolean;
  heloc_use_purpose?: HelocUsePurpose;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface HousingExpensesListResponse {
  housing_expenses: HousingExpense[];
  count: number;
  from?: string;
  to?: string;
}

export interface CreateHousingExpenseInput {
  expense_type: HousingExpenseType;
  amount_cents: number;
  payment_date: string;
  period_covered?: string;
  deductible_mortgage_interest_cents?: number;
  deductible_property_tax_cents?: number;
  non_deductible_portion_cents?: number;
  payment_account_id?: string;
  receipt_document_id?: string;
  vendor_or_payee?: string;
  is_recurring?: boolean;
  heloc_use_purpose?: HelocUsePurpose;
  notes?: string;
}

export interface UpdateHousingExpenseInput {
  expense_type?: HousingExpenseType;
  amount_cents?: number;
  period_covered?: string;
  deductible_mortgage_interest_cents?: number;
  deductible_property_tax_cents?: number;
  non_deductible_portion_cents?: number;
  payment_account_id?: string;
  receipt_document_id?: string;
  vendor_or_payee?: string;
  is_recurring?: boolean;
  heloc_use_purpose?: HelocUsePurpose;
  notes?: string;
}

export type AssessmentType =
  | "capital_improvement"
  | "operating_shortfall"
  | "facade_law_11"
  | "roof"
  | "elevator"
  | "lobby"
  | "boiler"
  | "legal"
  | "other";

export const ASSESSMENT_TYPE_LABELS: Readonly<Record<AssessmentType, string>> = {
  capital_improvement: "Capital improvement",
  operating_shortfall: "Operating shortfall",
  facade_law_11: "Façade Law 11 (FISP)",
  roof: "Roof",
  elevator: "Elevator",
  lobby: "Lobby",
  boiler: "Boiler",
  legal: "Legal",
  other: "Other",
};

export type AssessmentPaymentSchedule =
  | "lump_sum"
  | "monthly"
  | "quarterly"
  | "annual";

export const ASSESSMENT_PAYMENT_SCHEDULE_LABELS: Readonly<
  Record<AssessmentPaymentSchedule, string>
> = {
  lump_sum: "Lump sum",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

export interface HousingAssessment {
  assessment_id: string;
  unit_id: string;
  household_id: string;
  assessment_type: AssessmentType;
  total_amount_cents: number;
  per_share_cents?: number;
  start_date: string;
  end_date?: string;
  payment_schedule: AssessmentPaymentSchedule;
  remaining_cents?: number;
  payment_account_id?: string;
  document_id?: string;
  is_capitalizable_to_basis: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface HousingAssessmentsListResponse {
  housing_assessments: HousingAssessment[];
  count: number;
}

export interface CreateHousingAssessmentInput {
  assessment_type: AssessmentType;
  total_amount_cents: number;
  per_share_cents?: number;
  start_date: string;
  end_date?: string;
  payment_schedule: AssessmentPaymentSchedule;
  remaining_cents?: number;
  payment_account_id?: string;
  document_id?: string;
  is_capitalizable_to_basis?: boolean;
  notes?: string;
}

export interface UpdateHousingAssessmentInput {
  assessment_type?: AssessmentType;
  total_amount_cents?: number;
  per_share_cents?: number;
  start_date?: string;
  end_date?: string;
  payment_schedule?: AssessmentPaymentSchedule;
  remaining_cents?: number;
  payment_account_id?: string;
  document_id?: string;
  is_capitalizable_to_basis?: boolean;
  notes?: string;
}

// === Phase 5 Session 5 — 529 + HSA + UTMA + college plans =================

/**
 * Body for `POST /v1/accounts/{account_id}/transactions` — 529
 * contribution branch. Phase 5 Session 5 records contributions on the
 * `transactions` table with `transaction_type: "529_contribution"` so
 * tax-year aggregation lives at the read site (year-prefix on
 * timestamp). NY state $5K/$10K deduction math runs in
 * `web/src/lib/util/state-tax.ts`.
 */
export interface Create529ContributionInput {
  transaction_type: "529_contribution";
  amount_cents: number;
  contribution_date: string; // ISO YYYY-MM-DD; drives tax year
  notes?: string;
}

/**
 * HSA cashflow branches share a tx shape: contributions add cash to the
 * account; qualified distributions are tax-free against medical expenses;
 * non-qualified distributions are taxable + 20% penalty pre-65.
 */
export interface CreateHsaCashflowInput {
  transaction_type:
    | "hsa_contribution"
    | "hsa_distribution_qualified"
    | "hsa_distribution_non_qualified";
  amount_cents: number;
  /** Date of contribution / distribution (ISO YYYY-MM-DD). */
  date: string;
  notes?: string;
}

/** Status of an HSA receipt — see shared/src/schemas/health.ts. */
export type HsaReceiptStatus = "unreimbursed" | "reimbursed";

export interface HsaReceipt {
  receipt_id: string;
  household_id: string;
  account_id: string;
  service_date: string;
  amount_cents: number;
  provider: string;
  description?: string;
  is_qualified_medical: boolean;
  status: HsaReceiptStatus;
  reimbursed_date?: string;
  receipt_image_doc_id?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface HsaReceiptsListResponse {
  hsa_receipts: HsaReceipt[];
  count: number;
}

export interface CreateHsaReceiptInput {
  account_id: string;
  service_date: string;
  amount_cents: number;
  provider: string;
  description?: string;
  is_qualified_medical?: boolean;
  status?: HsaReceiptStatus;
  reimbursed_date?: string;
  receipt_image_doc_id?: string;
}

export interface UpdateHsaReceiptInput {
  service_date?: string;
  amount_cents?: number;
  provider?: string;
  description?: string;
  is_qualified_medical?: boolean;
  status?: HsaReceiptStatus;
  reimbursed_date?: string;
  receipt_image_doc_id?: string;
}

/**
 * `college_plans` row — Phase 5 Session 5 tightened the schema. The
 * projector itself (`web/src/lib/util/college-projector.ts`) consumes
 * these fields directly.
 */
export interface CollegePlan {
  plan_id: string;
  household_id: string;
  name: string;
  beneficiary_member_id: string;
  target_college_start_year: number;
  college_years: number;
  target_cost_today_cents: number;
  cost_inflation_pct: number;
  growth_rate_pct: number;
  monthly_contribution_cents?: number;
  linked_account_ids: string[];
  notes?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CollegePlansListResponse {
  college_plans: CollegePlan[];
  count: number;
}

export interface CreateCollegePlanInput {
  name: string;
  beneficiary_member_id: string;
  target_college_start_year: number;
  college_years?: number;
  target_cost_today_cents: number;
  cost_inflation_pct?: number;
  growth_rate_pct?: number;
  monthly_contribution_cents?: number;
  linked_account_ids?: string[];
  notes?: string;
}

export interface UpdateCollegePlanInput {
  name?: string;
  beneficiary_member_id?: string;
  target_college_start_year?: number;
  college_years?: number;
  target_cost_today_cents?: number;
  cost_inflation_pct?: number;
  growth_rate_pct?: number;
  monthly_contribution_cents?: number;
  linked_account_ids?: string[];
  notes?: string;
  is_archived?: boolean;
}

// === Phase 7 Session 1 — Tax ==============================================

export type TaxFilingStatus = "single" | "mfj" | "mfs" | "hoh" | "qss";
export type SafeHarborStatus =
  | "not_safe"
  | "90_pct"
  | "100_pct"
  | "110_pct";
export type TaxQuarter = "Q1" | "Q2" | "Q3" | "Q4";
export type TaxDocumentType =
  | "1099-DIV"
  | "1099-INT"
  | "1099-B"
  | "1099-R"
  | "1099-NEC"
  | "1099-MISC"
  | "1099-Q"
  | "1099-SA"
  | "5498"
  | "5498-SA"
  | "5498-ESA"
  | "1098"
  | "1098-T"
  | "1098-E"
  | "W-2"
  | "K-1"
  | "Schedule-D"
  | "Form-8949"
  | "Form-8889"
  | "Form-8606"
  | "other";
export type TaxDocumentSource =
  | "employer"
  | "brokerage"
  | "financial_institution"
  | "manual";
export type TaxDocumentStatus =
  | "expected"
  | "received"
  | "imported"
  | "reconciled";

export interface CapitalGainsPreviewLot {
  lot_id: string;
  account_id: string;
  account_type?: string;
  ticker: string;
  purchase_date: string;
  quantity: number;
  cost_basis_cents: number;
  current_value_cents: number;
  unrealized_gain_cents: number;
  term: "LT" | "ST";
  holding_days: number;
}

export interface CapitalGainsPreviewByAccount {
  account_id: string;
  account_type?: string;
  realized_st_cents: number;
  realized_lt_cents: number;
  unrealized_st_cents: number;
  unrealized_lt_cents: number;
}

export interface WashSaleCandidate {
  sale_tx_id: string;
  ticker: string;
  sale: {
    account_id: string;
    account_type?: string;
    sale_date: string;
    realized_loss_cents: number;
  };
  replacement: {
    lot_id: string;
    account_id: string;
    account_type?: string;
    acquired_date: string;
    quantity: number;
  };
  days_apart: number;
  scope: "same_account" | "cross_account";
}

export interface CapitalGainsPreviewResponse {
  realized_st_cents: number;
  realized_lt_cents: number;
  unrealized_st_cents: number;
  unrealized_lt_cents: number;
  total_realized_cents: number;
  total_unrealized_cents: number;
  by_account: CapitalGainsPreviewByAccount[];
  by_lot: CapitalGainsPreviewLot[];
  unpriced_lots: { lot_id: string; ticker: string }[];
  as_of: string;
  wash_sale_candidates: WashSaleCandidate[];
  wash_sale_safe_sales: number;
}

export interface Hsa8889Response {
  tax_year: number;
  coverage: "self_only" | "family";
  line_2_personal_contributions_cents: number;
  line_3_annual_limit_cents: number;
  line_5_after_archer_cents: number;
  line_6_after_spousal_cents: number;
  line_7_catch_up_cents: number;
  line_8_total_after_catchup_cents: number;
  line_9_employer_contributions_cents: number;
  line_10_qualified_funding_distribution_cents: number;
  line_11_employer_plus_funding_cents: number;
  line_12_max_personal_deduction_cents: number;
  line_13_hsa_deduction_cents: number;
  line_14a_total_distributions_cents: number;
  line_14b_rolled_over_cents: number;
  line_14c_net_distributions_cents: number;
  line_15_qualified_medical_cents: number;
  line_16_taxable_distributions_cents: number;
  line_17a_additional_20pct_tax_cents: number;
  catch_up_eligible: boolean;
  excess_contribution_cents: number;
  hsa_accounts_scanned: number;
}

export interface TaxDocument {
  tenant_id: string;
  household_id: string;
  year: string;
  doc_id: string;
  document_type: TaxDocumentType;
  source: TaxDocumentSource;
  status: TaxDocumentStatus;
  issuer?: string;
  related_account_id?: string;
  received_date?: string;
  file_s3_key?: string;
  notes?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface TaxDocumentsListResponse {
  tax_documents: TaxDocument[];
  count: number;
}

export interface CreateTaxDocumentInput {
  year: string;
  document_type: TaxDocumentType;
  source: TaxDocumentSource;
  status: TaxDocumentStatus;
  issuer?: string;
  related_account_id?: string;
  received_date?: string;
  file_s3_key?: string;
  notes?: string;
}

export interface UpdateTaxDocumentInput {
  document_type?: TaxDocumentType;
  source?: TaxDocumentSource;
  status?: TaxDocumentStatus;
  issuer?: string | null;
  related_account_id?: string | null;
  received_date?: string | null;
  file_s3_key?: string | null;
  notes?: string | null;
  is_archived?: boolean;
}

export interface TaxProjection {
  tenant_id: string;
  household_id: string;
  tax_year: string;
  quarter: TaxQuarter;
  projection_id: string;
  filing_status: TaxFilingStatus;
  ytd_ordinary_income_cents: number;
  ytd_ltcg_cents: number;
  ytd_qdivs_cents: number;
  projected_annual_ordinary_income_cents: number;
  projected_annual_ltcg_cents: number;
  projected_annual_qdivs_cents: number;
  projected_federal_liability_cents: number;
  projected_ny_liability_cents: number;
  projected_nyc_liability_cents: number;
  projected_total_liability_cents: number;
  estimated_tax_paid_cents?: number;
  prior_year_tax_cents?: number;
  prior_year_agi_cents?: number;
  safe_harbor_status: SafeHarborStatus;
  safe_harbor_required_payment_cents: number;
  composite_marginal_rate_pct: number;
  composite_effective_rate_pct: number;
  computed_at: string;
}

export interface TaxProjectionsListResponse {
  tax_projections: TaxProjection[];
  count: number;
}
